import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../Button';
import FamilyMemberRow from './FamilyMemberRow';
import {
  publicRegistrationSchema,
  type PublicRegistrationMember,
  type PublicRegistrationRequest,
  type PublicRegistrationResponse,
} from '../../types/public-registration';

interface Props {
  onSuccess?: (submissionId: string) => void;
}

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';
const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';

type VillaPreference = PublicRegistrationRequest['villaTypePreference'];

// Customer-facing framing: how many rooms the group needs in the house.
// Values are unchanged (backend still stores them as villa types).
const ROOM_OPTIONS: { value: VillaPreference; label: string; description: string }[] = [
  { value: 'standard-2bed', label: '2 rooms', description: 'For about 4 people' },
  { value: 'standard-3bed', label: '3 rooms', description: 'For about 6 people' },
  { value: 'host-villa', label: '4+ rooms', description: 'For larger parties' },
  { value: 'no-preference', label: 'No preference', description: 'Let the team decide' },
];

const emptyMember = (relationship: string): PublicRegistrationMember => ({
  firstName: '',
  lastName: '',
  ageGroup: 'adult',
  relationship,
  dietaryRestrictions: [],
});

export default function PublicRegistrationForm({ onSuccess }: Props) {
  const [leaderFirstName, setLeaderFirstName] = useState('');
  const [leaderLastName, setLeaderLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [familyConnection, setFamilyConnection] = useState('');
  const [villaTypePreference, setVillaTypePreference] = useState<VillaPreference>('no-preference');
  const [flexibilityNotes, setFlexibilityNotes] = useState('');
  const [roomConfigNotes, setRoomConfigNotes] = useState('');
  const [familyMembers, setFamilyMembers] = useState<PublicRegistrationMember[]>([emptyMember('self')]);
  const [clientError, setClientError] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<string | null>(null);

  const addMember = () => {
    if (familyMembers.length >= 20) return;
    setFamilyMembers([...familyMembers, emptyMember('other')]);
  };
  const removeMember = (i: number) =>
    setFamilyMembers(familyMembers.filter((_, idx) => idx !== i));
  const updateMember = (i: number, m: PublicRegistrationMember) =>
    setFamilyMembers(familyMembers.map((x, idx) => (idx === i ? m : x)));

  const submitMutation = useMutation<PublicRegistrationResponse, Error, PublicRegistrationRequest>({
    mutationFn: async (payload) => {
      const res = await fetch('/api/public/registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data?.error?.message || data?.message || `Submission failed (${res.status})`;
        throw new Error(message);
      }
      return data as PublicRegistrationResponse;
    },
    onSuccess: (data) => {
      const id = data.submissionId || '';
      setSubmittedId(id);
      onSuccess?.(id);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // The group leader IS the first party member — derive row 0's name from the leader fields
    // so they only enter it once, but they're still included in the registered party.
    const partyMembers = familyMembers.map((m, idx) =>
      idx === 0 ? { ...m, firstName: leaderFirstName, lastName: leaderLastName, relationship: 'self' } : m,
    );
    const payload: unknown = {
      leaderFirstName,
      leaderLastName,
      email,
      phone: phone || undefined,
      familyConnection: familyConnection || undefined,
      villaTypePreference,
      flexibilityNotes: flexibilityNotes || undefined,
      roomConfigNotes: roomConfigNotes || undefined,
      familyMembers: partyMembers,
    };
    const parsed = publicRegistrationSchema.safeParse(payload);
    if (!parsed.success) {
      setClientError(parsed.error.issues[0]?.message || 'Please check your entries.');
      return;
    }
    setClientError(null);
    submitMutation.mutate(parsed.data);
  };

  if (submittedId) {
    return (
      <div className="rounded-lg bg-success-light border border-success-border p-8 text-center">
        <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-2">Registration Received</h3>
        <p className="text-slate-600 text-sm mb-2">
          Thank you. Your family registration has been submitted for review. Our team will be in touch
          within 3 business days.
        </p>
        {submittedId && (
          <p className="text-xs text-slate-400">Reference: {submittedId.slice(0, 8)}</p>
        )}
      </div>
    );
  }

  return (
    <form className="space-y-8" onSubmit={handleSubmit} noValidate>
      {/* Group Leader */}
      <fieldset>
        <legend className="text-sm font-bold text-slate-900 mb-3">Group Leader (you)</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="leader-first" className={labelClasses}>First name</label>
            <input id="leader-first" type="text" value={leaderFirstName}
              onChange={(e) => setLeaderFirstName(e.target.value)} className={inputClasses} placeholder="First name" />
          </div>
          <div>
            <label htmlFor="leader-last" className={labelClasses}>Last name</label>
            <input id="leader-last" type="text" value={leaderLastName}
              onChange={(e) => setLeaderLastName(e.target.value)} className={inputClasses} placeholder="Last name" />
          </div>
          <div>
            <label htmlFor="leader-email" className={labelClasses}>Email</label>
            <input id="leader-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} className={inputClasses} placeholder="you@example.com" />
          </div>
          <div>
            <label htmlFor="leader-phone" className={labelClasses}>Phone (optional)</label>
            <input id="leader-phone" type="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)} className={inputClasses} placeholder="(555) 123-4567" />
          </div>
        </div>
        <div className="mt-5">
          <label htmlFor="leader-connection" className={labelClasses}>How is your family connected? (optional)</label>
          <textarea id="leader-connection" rows={3} maxLength={2000} value={familyConnection}
            onChange={(e) => setFamilyConnection(e.target.value)} className={`${inputClasses} resize-none`}
            placeholder="Tell us how you connect to the Goldson-Wade family." />
        </div>
      </fieldset>

      {/* Family Members */}
      <fieldset>
        <legend className="text-sm font-bold text-slate-900 mb-1">Your Party</legend>
        <p className="text-xs text-slate-500 mb-3">
          You're already included as the Group Leader below. Add anyone else attending with you.
        </p>
        <div className="space-y-3">
          {familyMembers.map((member, idx) => (
            <FamilyMemberRow
              key={idx}
              index={idx}
              member={
                idx === 0
                  ? { ...member, firstName: leaderFirstName, lastName: leaderLastName }
                  : member
              }
              onChange={(m) => updateMember(idx, m)}
              onRemove={idx === 0 ? undefined : () => removeMember(idx)}
              nameReadOnly={idx === 0}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={addMember}
          disabled={familyMembers.length >= 20}
          className="mt-3 text-sm font-bold text-primary hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Add family member
        </button>
      </fieldset>

      {/* Room accommodations */}
      <fieldset>
        <legend className="text-sm font-bold text-slate-900 mb-1">Room Accommodations</legend>
        <p className="text-xs text-slate-500 mb-3">About how many rooms will your group need in the house? This helps us make sure there's space for everyone.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ROOM_OPTIONS.map((opt) => {
            const selected = villaTypePreference === opt.value;
            return (
              <label
                key={opt.value}
                className={`flex cursor-pointer flex-col rounded-lg border p-4 transition-all focus-within:ring-2 focus-within:ring-primary ${
                  selected ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white hover:border-primary/50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="villaTypePreference"
                    value={opt.value}
                    checked={selected}
                    onChange={() => setVillaTypePreference(opt.value)}
                    className="text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-bold text-slate-800">{opt.label}</span>
                </span>
                <span className="ml-6 text-xs text-slate-500">{opt.description}</span>
              </label>
            );
          })}
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="villa-flex" className={labelClasses}>Flexibility notes (optional)</label>
            <textarea id="villa-flex" rows={2} maxLength={500} value={flexibilityNotes}
              onChange={(e) => setFlexibilityNotes(e.target.value)} className={`${inputClasses} resize-none`}
              placeholder="Open to fewer rooms if needed, etc." />
          </div>
          <div>
            <label htmlFor="villa-room" className={labelClasses}>Room configuration notes (optional)</label>
            <textarea id="villa-room" rows={2} maxLength={500} value={roomConfigNotes}
              onChange={(e) => setRoomConfigNotes(e.target.value)} className={`${inputClasses} resize-none`}
              placeholder="Need a crib in the master bedroom, kids share a room, etc." />
          </div>
        </div>
      </fieldset>

      {clientError && (
        <p className="text-error text-sm" role="alert">{clientError}</p>
      )}
      {submitMutation.isError && (
        <p className="text-error text-sm" role="alert">{submitMutation.error.message}</p>
      )}

      <Button type="submit" variant="primary" size="lg" className="w-full" disabled={submitMutation.isPending}>
        {submitMutation.isPending ? 'Submitting…' : 'Submit Registration'}
      </Button>
    </form>
  );
}
